import { lazy, type ReactElement, useCallback, useEffect, useState } from 'react';

import { Button, UploadInput } from '~components/generic/elements';
import {
  type ApiMediaUploadSettings,
  type FieldSettings,
  type FieldValue,
  type UploadFile,
} from '~modules/jsm-contact/shared/types';
// load atlaskit & co. first when a file is dropped
const UploadItem = lazy(() => import('./UploadItem'));

type FormFieldProps = {
  fieldSettings: FieldSettings;
  fieldValue: FieldValue;
  error?: string | null;
  onValueChanged: (key: string, value: FieldValue[]) => void;
  onError: () => void;
  isSubmitting: boolean;
  dataKey: string;
  apiMediaUploadSettings?: ApiMediaUploadSettings;
};

export function UploadGroup({
  fieldSettings,
  onValueChanged,
  onError,
  isSubmitting,
  dataKey,
  apiMediaUploadSettings,
}: FormFieldProps): ReactElement {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);

  const [droppedFiles, setDroppedFiles] = useState<File[] | null>(null);

  const getMaxKey = (uploadFiles: UploadFile[]): number => {
    return uploadFiles.reduce((acc: number, uploadFile: UploadFile) => {
      return Math.max(acc, uploadFile.key);
    }, -1);
  };

  useEffect(() => {
    if (droppedFiles) {
      let key = getMaxKey(uploadFiles);
      const droppedUploadFiles = [];
      for (const file of droppedFiles) {
        droppedUploadFiles.push({ file, assetId: undefined, key: ++key });
      }
      setUploadFiles([...uploadFiles, ...droppedUploadFiles]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles]);

  useEffect(() => {
    console.debug('UploadGroup: uploadFiles (useEffect, before onValueChanged):', uploadFiles);
    // pass only list of asset ids to the parent
    onValueChanged(
      fieldSettings.name,
      uploadFiles.map(uploadFile => uploadFile.assetId),
    );
  }, [onValueChanged, fieldSettings.name, uploadFiles]);

  const onUploadFinish = useCallback(
    (file: File, assetId: FieldValue) => {
      // set assetId for the corresponding file
      const uploadFilesNew = uploadFiles.map(uploadFile => {
        if (uploadFile.file === file) {
          uploadFile.assetId = assetId;
        }
        return uploadFile;
      });
      console.debug('UploadGroup: uploadFiles (onUploadFinish):', uploadFilesNew);
      setUploadFiles(uploadFilesNew);
    },
    [uploadFiles],
  );

  const onDelete = (file: File) => {
    const uploadFilesNew = uploadFiles.filter(uploadFile => uploadFile.file !== file);
    console.debug('UploadGroup: uploadFiles (onDelete):', uploadFilesNew);
    setUploadFiles(uploadFilesNew);
  };

  return (
    <div className="c-form-element">
      <div className="u-full-width flex flex-col align-items-center rounded border border-dashed border-silver">
        <div className="col col-12 p-0">
          <UploadInput
            // onClick={openFileDialog}
            onUpload={(files: File[]) => setDroppedFiles(files)}
            // label="Add or drop your assets here"
            // secondaryLabel={
            //  (fieldSettings.description ?? !apiMediaUploadSettings) ? 'File size should not exceed 5 MB' : undefined
            // }
            // icon={<IconPlus24 />}
            // withMenu={false}
            multiple={true}
            containerClassName={'u-image-ratio--4-1'}
          />
        </div>
      </div>
      <div className="space-y-3 py-2">
        {uploadFiles.map((uploadFile: UploadFile, index: number) => (
          <div key={uploadFile.key}>
            {index === 0 && <hr className="my-2" color="silver" />}
            <div className="flex flex-row">
              <UploadItem
                uploadFile={uploadFile}
                isSubmitting={isSubmitting}
                onUploadFinish={(assetId: FieldValue) => onUploadFinish(uploadFile.file, assetId)}
                onUploadError={onError}
                dataKey={dataKey}
                apiMediaUploadSettings={apiMediaUploadSettings}
              />
              <div className="w-1/4 flex justify-end align-items-start">
                <Button
                  className={'c-button mt-3 c-button--dark u-padding-horizontal-lg'}
                  // aspect="default"
                  // emphasis="strong"
                  // rounding="medium"
                  // size="medium"
                  type="button"
                  // variant="loud"
                  onClick={() => onDelete(uploadFile.file)}
                >
                  <>
                    <i className="c-icon-custom c-icon-custom--trash u-size-md" /> Remove
                  </>
                </Button>
              </div>
            </div>
            <hr className="my-2" color="silver" />
          </div>
        ))}
      </div>
    </div>
  );
}
